/**
 * Custom Addon Button Web Component
 * Handles toggle behavior for addon products - add on first click, remove on subsequent clicks
 * Uses variant-checkbox design pattern for consistent UI
 * Uses standard Shopify cart sections pattern for reliable UI updates
 * Supports products with multiple variants via expandable selector
 * 
 * NEW: Queue mode - addons are queued locally until main product is added to cart
 */

/**
 * AddonQueueManager - Singleton to manage queued addon selections
 * Tracks which addons are selected before the main product is added to cart
 */
const AddonQueueManager = {
  mainProductId: null,
  mainProductInCart: false,
  queuedAddons: new Map(), // variantId -> { variantId, productId }
  
  /**
   * Initialize the manager with the main product ID
   */
  init(mainProductId) {
    this.mainProductId = mainProductId;
  },
  
  /**
   * Check if main product is in cart and update state
   */
  async checkMainProductInCart() {
    if (!this.mainProductId) return false;
    
    try {
      const response = await fetch(`${window.routes.cart_url}.js`);
      const cart = await response.json();
      
      // Check if any variant of the main product is in cart
      const mainProductInCart = cart.items.some(item => 
        String(item.product_id) === String(this.mainProductId)
      );
      
      this.mainProductInCart = mainProductInCart;
      return mainProductInCart;
    } catch (error) {
      console.error('Error checking main product in cart:', error);
      return false;
    }
  },
  
  /**
   * Set main product in cart status (called after successful add)
   */
  setMainProductInCart(inCart) {
    this.mainProductInCart = inCart;
    
    // Dispatch event so addon buttons know to switch modes
    if (inCart) {
      document.dispatchEvent(new CustomEvent('mainProductAdded', {
        detail: { productId: this.mainProductId }
      }));
    }
  },
  
  /**
   * Queue an addon variant
   */
  queue(variantId, productId) {
    this.queuedAddons.set(String(variantId), { variantId: String(variantId), productId: String(productId) });
  },
  
  /**
   * Remove an addon from queue
   */
  dequeue(variantId) {
    this.queuedAddons.delete(String(variantId));
  },
  
  /**
   * Check if a variant is queued
   */
  isQueued(variantId) {
    return this.queuedAddons.has(String(variantId));
  },
  
  /**
   * Check if any variant of a product is queued
   */
  isProductQueued(productId) {
    for (const [, addon] of this.queuedAddons) {
      if (addon.productId === String(productId)) return true;
    }
    return false;
  },
  
  /**
   * Get queued variant ID for a product (for multi-variant addons)
   */
  getQueuedVariantForProduct(productId) {
    for (const [variantId, addon] of this.queuedAddons) {
      if (addon.productId === String(productId)) return variantId;
    }
    return null;
  },
  
  /**
   * Get all queued addon variant IDs
   */
  getQueuedVariantIds() {
    return Array.from(this.queuedAddons.keys());
  },
  
  /**
   * Clear all queued addons
   */
  clearQueue() {
    this.queuedAddons.clear();
  },
  
  /**
   * Check if we should be in queue mode
   */
  isQueueMode() {
    return !this.mainProductInCart;
  }
};

// Expose globally for product-form to access
window.AddonQueueManager = AddonQueueManager;

class CustomAddonButton extends HTMLElement {
  constructor() {
    super();
    this.variantId = null;
    this.lineKey = null;
    this.isInCart = false;
    this.isLoading = false;
    this.hasVariants = false;
    this.isExpanded = false;
    this.variants = [];
    this.variantInCartId = null; // Track which variant is actually in cart
    this.isQueued = false; // Track if addon is queued (not yet in cart)
    this.productId = null; // Track the addon's product ID
    this.mainProductId = null; // Track the main product ID this addon belongs to
  }

  connectedCallback() {
    this.variantId = this.dataset.variantId;
    this.hasVariants = this.dataset.hasVariants === 'true';
    this.productId = this.dataset.productId;
    this.mainProductId = this.dataset.mainProductId;
    
    // Initialize AddonQueueManager with main product ID if not already done
    if (this.mainProductId && !AddonQueueManager.mainProductId) {
      AddonQueueManager.init(this.mainProductId);
    }
    
    // Find the cart component (notification or drawer) like product-form does
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    
    if (this.hasVariants) {
      this.initMultiVariant();
    } else {
      this.initSingleVariant();
    }
    
    // Subscribe to cart updates from other sources (cart drawer, etc.)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    }
    
    // Listen for main product added event to switch from queue mode to cart mode
    this.mainProductAddedHandler = this.onMainProductAdded.bind(this);
    document.addEventListener('mainProductAdded', this.mainProductAddedHandler);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.mainProductAddedHandler) {
      document.removeEventListener('mainProductAdded', this.mainProductAddedHandler);
    }
  }

  /**
   * Handle main product added event - switch from queue mode to cart mode
   */
  onMainProductAdded(event) {
    // If this addon was queued, it should now be in the cart
    // The cart update will handle the state, but we need to clear our queued state
    if (this.isQueued) {
      this.isQueued = false;
      this.dataset.queued = 'false';
    }
    
    // Re-check cart state to update UI
    this.checkCartState();
  }

  /**
   * Initialize for single-variant add-ons (original behavior)
   */
  initSingleVariant() {
    // Find the label element (uses variant-checkbox structure)
    this.label = this.querySelector('.variant-checkbox__label');
    
    if (!this.label) return;
    
    // Check if disabled
    if (this.label.classList.contains('is-disabled')) return;
    
    // Check initial cart state (this also checks queue mode)
    this.checkCartState();
    
    // Listen for clicks on the label
    this.label.addEventListener('click', this.handleClick.bind(this));
    
    // Also listen for keyboard activation (Enter/Space)
    this.label.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  /**
   * Initialize for multi-variant add-ons
   */
  initMultiVariant() {
    // Parse variant data
    const variantsJson = this.querySelector('[data-addon-variants]');
    if (variantsJson) {
      try {
        this.variants = JSON.parse(variantsJson.textContent);
      } catch (e) {
        console.error('Failed to parse addon variants:', e);
      }
    }
    
    // Get DOM elements
    this.header = this.querySelector('[data-addon-header]');
    this.optionsContainer = this.querySelector('[data-addon-options]');
    this.priceDisplay = this.querySelector('[data-addon-price]');
    this.radioInputs = this.querySelectorAll('.addon-variant-selector__radio');
    this.label = this.header; // Use header as the main label for cart state
    
    if (!this.header || !this.optionsContainer) return;
    
    // Check if disabled
    if (this.header.classList.contains('is-disabled')) return;
    
    // Check initial cart state (this also checks queue mode)
    this.checkCartState();
    
    // Listen for header clicks to toggle expansion OR add/remove from cart
    this.header.addEventListener('click', this.handleMultiVariantHeaderClick.bind(this));
    this.header.addEventListener('keydown', this.handleKeydown.bind(this));
    
    // Listen for variant selection changes
    this.radioInputs.forEach(radio => {
      radio.addEventListener('change', this.handleVariantChange.bind(this));
    });
    
    // Prevent clicks inside options from bubbling to header
    this.optionsContainer.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.hasVariants) {
        this.handleMultiVariantHeaderClick(event);
      } else {
        this.handleClick(event);
      }
    }
  }

  /**
   * Handle header click for multi-variant add-ons
   * Queue mode: toggle expansion to select variant, then queue it
   * Cart mode: If in cart, remove; if not, toggle expansion
   */
  handleMultiVariantHeaderClick(event) {
    event.preventDefault();
    
    if (this.isLoading || this.header.classList.contains('is-disabled')) return;
    
    // In queue mode
    if (AddonQueueManager.isQueueMode()) {
      if (this.isQueued) {
        // If already queued, clicking header dequeues it
        this.handleDequeue();
      } else {
        // Toggle expansion to select variant
        this.toggleExpansion();
      }
    } else {
      // In cart mode (main product already in cart)
      if (this.isInCart) {
        // If already in cart, clicking header removes it
        this.handleRemoveFromCart();
      } else {
        // Toggle expansion
        this.toggleExpansion();
      }
    }
  }

  toggleExpansion() {
    this.isExpanded = !this.isExpanded;
    
    if (this.isExpanded) {
      this.optionsContainer.hidden = false;
      this.header.classList.add('is-expanded');
      // Small delay for animation
      requestAnimationFrame(() => {
        this.optionsContainer.classList.add('is-open');
      });
    } else {
      this.optionsContainer.classList.remove('is-open');
      this.header.classList.remove('is-expanded');
      // Wait for animation to complete before hiding
      setTimeout(() => {
        if (!this.isExpanded) {
          this.optionsContainer.hidden = true;
        }
      }, 200);
    }
  }

  /**
   * Handle variant radio button selection
   * Queue mode: queue the selected variant
   * Cart mode: add to cart
   */
  handleVariantChange(event) {
    const radio = event.target;
    const newVariantId = radio.value;
    const priceFormatted = radio.dataset.priceFormatted;
    
    // Update variant ID
    this.variantId = newVariantId;
    this.dataset.variantId = newVariantId;
    
    // Update price display
    if (this.priceDisplay && priceFormatted) {
      this.priceDisplay.textContent = '+ ' + priceFormatted;
    }
    
    // Update selected state on options
    this.querySelectorAll('.addon-variant-selector__option').forEach(option => {
      option.classList.remove('is-selected');
    });
    radio.closest('.addon-variant-selector__option').classList.add('is-selected');
    
    // Queue mode: queue the variant; Cart mode: add to cart
    if (AddonQueueManager.isQueueMode()) {
      this.handleQueue();
      // Collapse the options after queuing
      if (this.isExpanded) {
        this.toggleExpansion();
      }
    } else {
      this.handleAddToCart();
    }
  }

  /**
   * Queue the addon (for queue mode)
   */
  handleQueue() {
    // If a different variant was queued, dequeue it first
    const existingQueued = AddonQueueManager.getQueuedVariantForProduct(this.productId);
    if (existingQueued && existingQueued !== this.variantId) {
      AddonQueueManager.dequeue(existingQueued);
    }
    
    AddonQueueManager.queue(this.variantId, this.productId);
    this.isQueued = true;
    this.dataset.queued = 'true';
    
    // Update UI to show queued state
    if (this.hasVariants) {
      this.header.classList.add('is-selected');
    } else {
      this.label.classList.add('is-selected');
    }
  }

  /**
   * Dequeue the addon (for queue mode)
   */
  handleDequeue() {
    AddonQueueManager.dequeue(this.variantId);
    this.isQueued = false;
    this.dataset.queued = 'false';
    
    // Update UI to show dequeued state
    if (this.hasVariants) {
      this.header.classList.remove('is-selected');
    } else {
      this.label.classList.remove('is-selected');
    }
  }

  /**
   * Handle add to cart for multi-variant
   */
  async handleAddToCart() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.header.classList.add('is-loading');
    
    try {
      // If a different variant is in cart, remove it first
      if (this.isInCart && this.variantInCartId && String(this.variantInCartId) !== String(this.variantId)) {
        await this.removeFromCart();
      }
      
      await this.addToCart();
      
      // Collapse the options after successful add
      if (this.isExpanded) {
        this.toggleExpansion();
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      this.isLoading = false;
      this.header.classList.remove('is-loading');
    }
  }

  /**
   * Handle remove from cart for multi-variant
   */
  async handleRemoveFromCart() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.header.classList.add('is-loading');
    
    try {
      await this.removeFromCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      this.isLoading = false;
      this.header.classList.remove('is-loading');
    }
  }

  async checkCartState() {
    try {
      const response = await fetch(`${window.routes.cart_url}.js`);
      const cart = await response.json();
      
      // First, check if main product is in cart to determine mode
      if (this.mainProductId) {
        const mainProductInCart = cart.items.some(item => 
          String(item.product_id) === String(this.mainProductId)
        );
        AddonQueueManager.mainProductInCart = mainProductInCart;
      }
      
      this.updateStateFromCart(cart);
      
      // If in queue mode, restore queued state from AddonQueueManager
      if (AddonQueueManager.isQueueMode()) {
        this.restoreQueuedState();
      }
    } catch (error) {
      console.error('Error checking cart state:', error);
    }
  }

  /**
   * Restore queued state from AddonQueueManager (for page refresh scenarios)
   */
  restoreQueuedState() {
    if (this.hasVariants) {
      const queuedVariant = AddonQueueManager.getQueuedVariantForProduct(this.productId);
      if (queuedVariant) {
        this.isQueued = true;
        this.variantId = queuedVariant;
        this.dataset.variantId = queuedVariant;
        this.dataset.queued = 'true';
        this.header.classList.add('is-selected');
        
        // Update radio button
        const matchingRadio = this.querySelector(`input[value="${queuedVariant}"]`);
        if (matchingRadio) {
          matchingRadio.checked = true;
          this.querySelectorAll('.addon-variant-selector__option').forEach(option => {
            option.classList.remove('is-selected');
          });
          matchingRadio.closest('.addon-variant-selector__option').classList.add('is-selected');
          
          if (this.priceDisplay && matchingRadio.dataset.priceFormatted) {
            this.priceDisplay.textContent = '+ ' + matchingRadio.dataset.priceFormatted;
          }
        }
      }
    } else {
      if (AddonQueueManager.isQueued(this.variantId)) {
        this.isQueued = true;
        this.dataset.queued = 'true';
        this.label.classList.add('is-selected');
      }
    }
  }

  updateStateFromCart(cart) {
    // Defensive check - cart.items might not exist if this is a cart add response
    if (!cart || !Array.isArray(cart.items)) {
      return false; // Signal that we couldn't update from this data
    }
    
    if (this.hasVariants) {
      // For multi-variant, check if ANY variant of this product is in cart
      const variantIds = this.variants.map(v => String(v.id));
      const cartItem = cart.items.find(item => variantIds.includes(String(item.variant_id)));
      
      if (cartItem) {
        this.isInCart = true;
        this.lineKey = cartItem.key;
        this.variantInCartId = cartItem.variant_id;
        this.variantId = String(cartItem.variant_id);
        this.header.classList.add('is-selected');
        
        // Clear queued state since it's now in cart
        this.isQueued = false;
        this.dataset.queued = 'false';
        
        // Update the radio button to match what's in cart
        const matchingRadio = this.querySelector(`input[value="${cartItem.variant_id}"]`);
        if (matchingRadio && !matchingRadio.checked) {
          matchingRadio.checked = true;
          // Update UI
          this.querySelectorAll('.addon-variant-selector__option').forEach(option => {
            option.classList.remove('is-selected');
          });
          matchingRadio.closest('.addon-variant-selector__option').classList.add('is-selected');
          
          // Update price display
          if (this.priceDisplay && matchingRadio.dataset.priceFormatted) {
            this.priceDisplay.textContent = '+ ' + matchingRadio.dataset.priceFormatted;
          }
        }
      } else {
        this.isInCart = false;
        this.lineKey = null;
        this.variantInCartId = null;
        // Only remove selected class if not queued
        if (!this.isQueued) {
          this.header.classList.remove('is-selected');
        }
      }
    } else {
      // Single variant behavior (original)
      const cartItem = cart.items.find(item => String(item.variant_id) === String(this.variantId));
      
      if (cartItem) {
        this.isInCart = true;
        this.lineKey = cartItem.key;
        this.label.classList.add('is-selected');
        
        // Clear queued state since it's now in cart
        this.isQueued = false;
        this.dataset.queued = 'false';
      } else {
        this.isInCart = false;
        this.lineKey = null;
        // Only remove selected class if not queued
        if (!this.isQueued) {
          this.label.classList.remove('is-selected');
        }
      }
    }
    
    return true; // Successfully updated
  }

  onCartUpdate(event) {
    // When cart updates from elsewhere, re-check our state
    // The cartData might be a full cart object or a cart add response (different structure)
    if (event.cartData) {
      const updated = this.updateStateFromCart(event.cartData);
      if (!updated) {
        // If the data structure wasn't a full cart, fetch the full cart
        this.checkCartState();
      }
    } else {
      // If no cart data provided, fetch it
      this.checkCartState();
    }
  }

  async handleClick(event) {
    event.preventDefault();
    
    if (this.isLoading || this.label.classList.contains('is-disabled')) return;
    
    // Queue mode: toggle queue state
    if (AddonQueueManager.isQueueMode()) {
      if (this.isQueued) {
        this.handleDequeue();
      } else {
        this.handleQueue();
      }
      return;
    }
    
    // Cart mode: add/remove from cart
    this.isLoading = true;
    this.label.classList.add('is-loading');
    
    try {
      if (this.isInCart) {
        await this.removeFromCart();
      } else {
        await this.addToCart();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      this.isLoading = false;
      this.label.classList.remove('is-loading');
    }
  }

  getSectionsToRender() {
    // Use the cart component's sections if available, otherwise provide defaults
    if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
      return this.cart.getSectionsToRender().map((section) => section.id);
    }
    // Fallback sections for cart bubble update
    return ['cart-icon-bubble'];
  }

  async addToCart() {
    const sections = this.getSectionsToRender();
    
    const response = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: this.variantId,
          quantity: 1
        }],
        sections: sections,
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    const result = await response.json();
    
    // Get the line key from the response
    if (result.items && result.items.length > 0) {
      this.lineKey = result.items[0].key;
      this.variantInCartId = result.items[0].variant_id;
    }
    
    this.isInCart = true;
    
    if (this.hasVariants) {
      this.header.classList.add('is-selected');
    } else {
      this.label.classList.add('is-selected');
    }
    
    // Update cart UI using the standard pattern
    this.renderCartContents(result, false);
  }

  async removeFromCart() {
    if (!this.lineKey) {
      // If we don't have a line key, fetch cart to find it
      await this.checkCartState();
      if (!this.lineKey) {
        console.error('Cannot remove: no line key found');
        return;
      }
    }

    const sections = this.getSectionsToRender();

    const response = await fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: this.lineKey,
        quantity: 0,
        sections: sections,
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) {
      throw new Error('Failed to remove from cart');
    }

    const result = await response.json();

    this.isInCart = false;
    this.lineKey = null;
    this.variantInCartId = null;
    
    if (this.hasVariants) {
      this.header.classList.remove('is-selected');
    } else {
      this.label.classList.remove('is-selected');
    }
    
    // Update cart UI using the standard pattern
    this.renderCartContents(result, true);
  }

  renderCartContents(response, isRemoval = false) {
    // If we have a cart component with renderContents, use it (standard pattern)
    if (this.cart && typeof this.cart.renderContents === 'function' && response.sections) {
      // For cart-notification, we need to set the key for proper rendering
      if (response.items && response.items.length > 0) {
        response.key = response.items[0].key;
      }
      this.cart.renderContents(response);
      
      // Update notification heading based on action type
      this.updateNotificationHeading(isRemoval);
    } else if (response.sections) {
      // Fallback: manually update the cart icon bubble
      this.updateCartIconBubble(response.sections);
    }
    
    // Publish cart update event for other components
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { 
        source: 'custom-addons', 
        cartData: response 
      });
    }
  }

  /**
   * Update the cart notification heading text for add/remove actions
   */
  updateNotificationHeading(isRemoval) {
    if (!this.cart) return;
    
    const heading = this.cart.querySelector('.cart-notification__heading');
    if (!heading) return;
    
    const itemAddedText = window.cartStrings?.itemAdded || 'Item added to your cart';
    const itemRemovedText = window.cartStrings?.itemRemoved || 'Item removed from cart';
    
    // Get the checkmark SVG to preserve it
    const svg = heading.querySelector('svg') || heading.querySelector('.icon');
    const svgHtml = svg ? svg.outerHTML : '';
    
    if (isRemoval) {
      heading.innerHTML = svgHtml + ' ' + itemRemovedText;
    } else {
      heading.innerHTML = svgHtml + ' ' + itemAddedText;
    }
  }

  updateCartIconBubble(sections) {
    const cartIconBubbleHtml = sections['cart-icon-bubble'];
    if (!cartIconBubbleHtml) return;
    
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(cartIconBubbleHtml, 'text/html');
      const newContent = doc.querySelector('.shopify-section');
      if (newContent) {
        cartIconBubble.innerHTML = newContent.innerHTML;
      }
    }
  }
}

// Only define if not already defined
if (!customElements.get('custom-addon-button')) {
  customElements.define('custom-addon-button', CustomAddonButton);
}
